import React, { useCallback, useEffect, useMemo, useState } from 'react'

import ApiClient from '../../services/api/ApiClient'
import Spinner from '../ui/Spinner'
import SaveFooter from '../shared/SaveFooter'
import AnswerFormHeader from '../AnswerForm/AnswerFormHeader'
import SectionBlock from '../AnswerForm/SectionBlock'
import buildMarksFormData from '../AnswerForm/buildMarksFormData'
import { hiddenByParallel } from '../AnswerForm/conditionalVisibility'
import { pendingPinErrorsByParallel } from '../AnswerForm/pendingPinErrors'
import scrollToFirstError from '../../utils/scrollToFirstError'

const updateMark = (parallels, markId, changes) => parallels.map(parallel => ({
  ...parallel,
  marks: parallel.marks.map(mark => {
    if (mark.id === markId)
      return { ...mark, ...changes }
    return mark
  })
}))

const EditAnswer = () => {
  const [answer, setAnswer] = useState(null)
  const [errors, setErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [collapsedIds, setCollapsedIds] = useState({})
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    const url = `${window.location.pathname}.json${window.location.search}`
    ApiClient.get(url).then(setAnswer).catch(() => setLoadError(true))
  }, [])

  const handleMarkChange = useCallback((markId, changes) => {
    setAnswer(current => ({ ...current, parallels: updateMark(current.parallels, markId, changes) }))
  }, [])

  const toggleSection = useCallback(id => {
    setCollapsedIds(current => ({ ...current, [id]: !current[id] }))
  }, [])

  const handleCancel = useCallback(() => {
    window.location.assign(answer.back_url)
  }, [answer])

  const handleSubmit = useCallback(async() => {
    const pending = pendingPinErrorsByParallel(answer.parallels)
    if (Object.keys(pending).length > 0) {
      setErrors(pending)
      scrollToFirstError()
      return
    }

    const formData = new FormData()
    answer.parallels.forEach((parallel, index) => {
      const base = `answer[parallels_attributes][${index}]`
      formData.append(`${base}[id]`, parallel.id)
      buildMarksFormData(formData, base, parallel.marks)
    })

    setSubmitting(true)
    setErrors({})

    try {
      const url = `${answer.form_url}${window.location.search}`
      const data = await ApiClient.patchFormData(url, formData)
      if (data.success) {
        window.location.assign(data.redirect_url)
        return
      }
      setErrors(data.formatted_errors || {})
      setCollapsedIds({})
      setSubmitting(false)
      scrollToFirstError()
    } catch (error) {
      setSubmitting(false)
      setErrors({ base: [error.message] })
      scrollToFirstError()
    }
  }, [answer])

  const hiddenMaps = useMemo(() => {
    if (!answer)
      return {}
    return hiddenByParallel(answer.conditional_visibility, answer.parallels)
  }, [answer])

  if (loadError) {
    return (
      <div className="alert alert-danger" role="alert">
        Le chargement a échoué. Veuillez recharger la page.
      </div>
    )
  }

  if (!answer) {
    return (
      <div className="tw:flex tw:justify-center tw:py-5">
        <Spinner />
      </div>
    )
  }

  return (
    <div>
      <AnswerFormHeader backUrl={answer.back_url} chips={[answer.collect.name, answer.resource.name]} />
      {errors.base ? (
        <div className="alert alert-danger" role="alert">
          {errors.base.join(', ')}
        </div>
      ) : null}
      {answer.parallels.map(parallel => (
        <SectionBlock
          key={parallel.id}
          collapsed={Boolean(collapsedIds[parallel.id])}
          collapsible={answer.parallels.length > 1}
          errors={errors[`parallels[${parallel.id}]`]}
          hidden={hiddenMaps[parallel.id] || {}}
          parallel={parallel}
          readonly={answer.readonly}
          onMarkChange={handleMarkChange}
          onToggle={toggleSection}
        />
      ))}
      {answer.readonly ? null : (
        <SaveFooter
          disabled={submitting}
          submitText={submitting ? 'Patientez...' : 'Sauvegarder'}
          onCancel={handleCancel}
          onSave={handleSubmit}
        />
      )}
    </div>
  )
}

export default EditAnswer
